import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import LoginIcon from "@mui/icons-material/Login";
import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Logo } from "@/components/brand/Logo";

type NavLinkItem = { to: string; label: string };

// Link sets are chosen per active role rather than stacked on top of each
// other. Our roles are parallel (a doctor account is not also a patient),
// so the navbar only ever advertises flows that make sense for the
// signed-in user. Guests fall back to the patient-facing set because
// they're the audience we're trying to convert.
const guestAndPatientLinks: NavLinkItem[] = [
  { to: "/", label: "Home" },
  { to: "/doctors", label: "Find a doctor" },
  { to: "/how-it-works", label: "How it works" },
];

const doctorLinks: NavLinkItem[] = [
  { to: "/doctor/dashboard", label: "Home" },
  { to: "/how-it-works", label: "How it works" },
];

const adminLinks: NavLinkItem[] = [
  { to: "/admin/doctors", label: "Home" },
];

export function Navbar() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const {
    isAuthenticated,
    fullName,
    username,
    email,
    isPatient,
    isDoctor,
    isAdmin,
    login,
    register,
    logout,
  } = useAuth();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [mobileAnchor, setMobileAnchor] = useState<HTMLElement | null>(null);

  const dashboardPath = isDoctor
    ? "/doctor/dashboard"
    : isAdmin
      ? "/admin/doctors"
      : "/patient/dashboard";

  const navLinks: NavLinkItem[] = isDoctor
    ? doctorLinks
    : isAdmin
      ? adminLinks
      : guestAndPatientLinks;

  const displayName = fullName || username || email || "Account";
  const initial = (displayName[0] || "U").toUpperCase();

  return (
    <AppBar position="sticky" color="default" elevation={0}>
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: 72, gap: 2 }}>
          <Logo size={40} compact={!isDesktop} />

          {isDesktop && (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ ml: 4, flex: 1 }}
              component="nav"
              aria-label="Primary"
            >
              {navLinks.map((l) => (
                <Button
                  key={l.to}
                  component={NavLink}
                  to={l.to}
                  end={l.to === "/"}
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    px: 1.5,
                    "&.active": { color: "primary.dark" },
                  }}
                >
                  {l.label}
                </Button>
              ))}
            </Stack>
          )}

          <Box sx={{ flex: isDesktop ? undefined : 1 }} />

          {!isAuthenticated && (
            <Stack direction="row" spacing={1}>
              {isDesktop ? (
                <>
                  <Button
                    onClick={() => login("/")}
                    startIcon={<LoginIcon />}
                    color="inherit"
                    sx={{ color: "text.primary" }}
                  >
                    Log in
                  </Button>
                  <Button
                    onClick={() => register("/patient/dashboard")}
                    variant="contained"
                    color="primary"
                  >
                    Get started
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => login("/")}
                  variant="contained"
                  color="primary"
                  size="small"
                >
                  Log in
                </Button>
              )}
            </Stack>
          )}

          {isAuthenticated && (
            <Stack direction="row" spacing={1} alignItems="center">
              {isDesktop && (
                <Button
                  onClick={() => navigate(dashboardPath)}
                  startIcon={<DashboardIcon />}
                  variant="outlined"
                  color="primary"
                >
                  Dashboard
                </Button>
              )}
              <Tooltip title={displayName}>
                <IconButton
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={{ p: 0.5 }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      width: 40,
                      height: 40,
                      fontWeight: 700,
                    }}
                  >
                    {initial}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                slotProps={{ paper: { sx: { mt: 1, minWidth: 220 } } }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Box sx={{ fontWeight: 700 }}>{displayName}</Box>
                  <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                    {isDoctor
                      ? "Doctor"
                      : isAdmin
                        ? "Admin"
                        : isPatient
                          ? "Patient"
                          : "User"}
                  </Box>
                </Box>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    navigate(dashboardPath);
                  }}
                >
                  <ListItemIcon>
                    <DashboardIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Dashboard</ListItemText>
                </MenuItem>
                {isPatient && (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      navigate("/patient/appointments");
                    }}
                  >
                    <ListItemIcon>
                      <EventAvailableIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>My appointments</ListItemText>
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    navigate(isDoctor ? "/doctor/profile" : "/patient/profile");
                  }}
                >
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Profile</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => logout()}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Sign out</ListItemText>
                </MenuItem>
              </Menu>
            </Stack>
          )}

          {!isDesktop && (
            <IconButton
              edge="end"
              onClick={(e) => setMobileAnchor(e.currentTarget)}
              aria-label="Open navigation menu"
            >
              <MenuIcon />
            </IconButton>
          )}
          <Menu
            anchorEl={mobileAnchor}
            open={Boolean(mobileAnchor)}
            onClose={() => setMobileAnchor(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {navLinks.map((l) => (
              <MenuItem
                key={l.to}
                onClick={() => {
                  setMobileAnchor(null);
                  navigate(l.to);
                }}
              >
                {l.label}
              </MenuItem>
            ))}
            {!isAuthenticated && (
              <MenuItem
                onClick={() => {
                  setMobileAnchor(null);
                  register("/patient/dashboard");
                }}
              >
                <ListItemIcon>
                  <HowToRegIcon fontSize="small" />
                </ListItemIcon>
                Create account
              </MenuItem>
            )}
          </Menu>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
